import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { RotateCcw, Send, Sparkles, X } from "lucide-react";
import { distinctClarification, useConsultant } from "./ConsultantContext";
import ConsultantReportCharts from "./ConsultantReportCharts";

const Message = ({ message, onAction }) => {
  const clarification = distinctClarification(
    message.content,
    message.clarificationQuestion,
  );
  return (
    <article className={`consultant-message consultant-message--${message.role}`}>
    <div className="consultant-message__content">
      {message.role === "assistant" ? (
        <ReactMarkdown
          components={{
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noreferrer">{children}</a>
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>
      ) : message.content}
    </div>
    {message.role === "assistant" && message.reportCharts?.length > 0 && (
      <ConsultantReportCharts charts={message.reportCharts} />
    )}
    {clarification && (
      <div className="consultant-clarification" data-testid="consultant-clarification">
        {clarification}
      </div>
    )}
    {message.suggestedActions?.length > 0 && (
      <div className="consultant-followups" aria-label="Preguntas sugeridas">
        {message.suggestedActions.map((action) => (
          <button type="button" key={action} onClick={() => onAction(action)}>{action}</button>
        ))}
      </div>
    )}
    </article>
  );
};

const ConsultantChatPanel = () => {
  const {
    close,
    messages,
    loading,
    error,
    contextualSuggestions,
    sendMessage,
    resetConversation,
    dismissError,
  } = useConsultant();
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [messages, loading, error]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const submit = async (value = draft, preserveExact = false) => {
    const sourceText = String(value || "");
    const text = preserveExact ? sourceText : sourceText.trim();
    if (!text.trim() || loading) return;
    setDraft("");
    await sendMessage(text, { preserveExact });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <section className="consultant-panel" role="dialog" aria-modal="false" aria-label="Consultor SIGER">
      <header className="consultant-panel__header">
        <div className="consultant-panel__identity">
          <span className="consultant-panel__mark"><Sparkles size={17} /></span>
          <div>
            <h2>Consultor SIGER</h2>
            <span>Asistente energético</span>
          </div>
        </div>
        <div className="consultant-panel__controls">
          <button type="button" onClick={resetConversation} title="Nueva conversación" aria-label="Nueva conversación">
            <RotateCcw size={18} />
          </button>
          <button type="button" onClick={close} title="Cerrar" aria-label="Cerrar consultor">
            <X size={20} />
          </button>
        </div>
      </header>

      <div className="consultant-panel__messages" aria-live="polite">
        {messages.length === 0 && (
          <div className="consultant-welcome">
            <span><Sparkles size={19} /></span>
            <h3>¿Qué querés analizar?</h3>
            <p>Puedo ayudarte a interpretar consumos, demanda, reactiva, facturación y costos con el contexto de esta pantalla.</p>
          </div>
        )}
        {messages.map((message) => (
          <Message
            key={message.id}
            message={message}
            onAction={(action) => submit(action, true)}
          />
        ))}
        {loading && (
          <div className="consultant-typing" aria-label="El consultor está respondiendo">
            <span /><span /><span />
          </div>
        )}
        {error && (
          <div className="consultant-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={dismissError} aria-label="Cerrar aviso">×</button>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length === 0 && (
        <div className="consultant-suggestions" aria-label="Sugerencias para esta pantalla">
          {contextualSuggestions.map((suggestion) => (
            <button type="button" key={suggestion} onClick={() => submit(suggestion, true)}>{suggestion}</button>
          ))}
        </div>
      )}

      <footer className="consultant-composer">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribí una consulta..."
          aria-label="Consulta para el consultor"
          maxLength={8000}
          rows={1}
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => submit()}
          disabled={loading || !draft.trim()}
          aria-label="Enviar consulta"
          title="Enviar"
        >
          <Send size={18} />
        </button>
      </footer>
      <div className="consultant-disclaimer">Las respuestas se basan en los datos disponibles en SIGER.</div>
    </section>
  );
};

export default ConsultantChatPanel;
